// §34.11 (D-335, D-565) — calendar reminders, owned by the **shell** rather than by the app.
//
// §34.11 asks for two things that together decide where this code lives: a reminder "schedules the
// next occurrence's instant through the kernel alarm service (instants, kernel-owned, survive
// restart)", and "firing … schedules the following occurrence". The second is what rules out doing it
// in the app: a weekly reminder has to arm next week's instance, and the window that would do it is
// shut. So the schedule is the kernel's, exactly as §11.14's alarms are and for the same reason.
//
// ── Why this holds no schedule of its own
//
// The obvious design is a list of armed instants persisted beside the alarms. This does not do that,
// and the reason is worth stating: **the events file already is the schedule.** Every reminder instant
// is a pure function of an event's `start`, its `recur` and its `reminderMin` (`nextReminderAt` in
// `shared/events.ts`), so a second stored copy could only ever *disagree* with it — after an edit, a
// sync from another device (§7.4), or a `.ics` import. Deriving on every check cannot drift.
//
// What is persisted is one number per event: **the last instant already announced.** That is the only
// fact not recoverable from the events file, and it is what makes the difference between "this reminder
// has not fired yet" and "this reminder fired while the tab was shut".
//
// ── What happens to a reminder that came due while the tab was closed
//
// It fires, late, on the next boot — D-335's rule, inherited deliberately: *"Late is useful; silent is
// not."* The watermark is what stops it firing twice.

import { nextReminderAt, type CalendarEvent } from '@hangar/shared';

/** How often the schedule is checked. One second, matching §11.14's alarm tick. */
export const CALENDAR_TICK_MS = 1_000;

/**
 * Where the announced-watermarks live.
 *
 * Under `/System`, reached through `vfs.internal` — **not** under `/Apps/clock/`, and the distinction
 * is the point: the events are the *user's* data and sync between their devices (§7.4), whereas a
 * watermark is this device's record of what it has already shown. Syncing it would mean a reminder
 * announced on a laptop is silently suppressed on a phone that was closed at the time, which is the
 * opposite of D-335's "late is useful".
 */
export const CALENDAR_WATERMARKS_PATH = '/System/.calendar/announced.json';

/** A reminder that has come due, as the shell announces it. */
export interface DueReminder {
  readonly event: CalendarEvent;
  /** The instant the reminder was due — not `now`, which may be later if the tab was shut. */
  readonly at: number;
  /** The occurrence's own start instant, so the notification can say when the thing is. */
  readonly startsAt: number;
  /** How late the announcement is, in milliseconds. Zero in the ordinary case. */
  readonly lateMs: number;
}

/** Where the watermarks live. Injected, so a test needs no filesystem. */
export interface ReminderWatermarkStore {
  read(): Promise<unknown>;
  write(value: unknown): Promise<void>;
}

export interface CalendarRemindersOptions {
  /** The events, read fresh on every check — the file is the schedule (see the header). */
  readEvents: () => Promise<readonly CalendarEvent[]>;
  watermarks: ReminderWatermarkStore;
  /** §13 — how a reminder reaches somebody. Injected so a test never needs the notify service. */
  announce: (due: DueReminder) => void;
  /** §34.16 `WB_CAL_REMINDER_HORIZON_DAYS`. */
  horizonDays: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (handle: number) => void;
  onError?: (error: unknown) => void;
}

export interface CalendarRemindersService {
  /** Read the watermarks and catch up on anything missed. Idempotent; safe to call once at boot. */
  hydrate(): Promise<void>;
  /** Begin checking. Idempotent. */
  start(): void;
  stop(): void;
  /** One pass. Exposed so a test can drive it with a mocked clock instead of waiting. */
  check(): Promise<void>;
  /** The next instant each event will announce, for the Inspector and the tests. */
  upcoming(): Promise<{ eventId: string; at: number }[]>;
}

interface Stored {
  /** eventId → the last reminder instant announced for it. */
  readonly announced?: Record<string, number>;
}

export function createCalendarReminders(
  options: CalendarRemindersOptions,
): CalendarRemindersService {
  const now = options.now ?? (() => Date.now());
  const setTimer =
    options.setTimer ?? ((fn, ms) => globalThis.setInterval(fn, ms) as unknown as number);
  const clearTimer = options.clearTimer ?? ((handle) => globalThis.clearInterval(handle));
  const report = options.onError ?? ((error: unknown) => console.warn('calendar reminders', error));

  /** eventId → the last reminder instant announced. */
  let announced = new Map<string, number>();
  let timer: number | null = null;
  let hydrated = false;
  /** One check at a time: each reads the events file, and two overlapping passes could double-fire. */
  let running: Promise<void> | null = null;

  const persist = (): void => {
    void options.watermarks
      .write({ announced: Object.fromEntries(announced) })
      .catch((error: unknown) => report(error));
  };

  async function runCheck(): Promise<void> {
    const at = now();
    let events: readonly CalendarEvent[];
    try {
      events = await options.readEvents();
    } catch (error) {
      // No calendar file yet is the ordinary first-run case, and an unreadable one must not stop the
      // desktop. The next tick tries again.
      report(error);
      return;
    }

    let changed = false;
    const live = new Set<string>();

    for (const event of events) {
      if (event.reminderMin === undefined) continue;
      live.add(event.id);
      const since = announced.get(event.id) ?? 0;

      // Look from **the watermark**, not from `now`: that is what makes a reminder whose instant
      // passed while the tab was shut still due, and it is D-335's rule rather than a nicety.
      //
      // The horizon is widened by the gap so a long-closed tab still finds the missed instant —
      // otherwise a reminder missed by more than the horizon would be skipped in silence, which is
      // the one outcome the whole design is against.
      const lookFrom = Math.max(0, since);
      const gapDays = Math.ceil(Math.max(0, at - lookFrom) / 86_400_000);
      const fire = nextReminderAt(event, lookFrom, options.horizonDays + gapDays);
      if (fire === null || fire > at) continue;

      // Fire the **earliest** unannounced instant, and only one per pass. A tab shut for a month
      // should not produce thirty notifications in one tick for a daily reminder; it produces one,
      // and the next tick produces the next, which self-limits at one per second and stops as soon as
      // the watermark catches up to the present.
      announced.set(event.id, fire);
      changed = true;

      const startsAt = fire + event.reminderMin * 60_000;
      options.announce({ event, at: fire, startsAt, lateMs: Math.max(0, at - fire) });
    }

    // An event that was deleted, or whose reminder was removed, keeps no watermark: the map would
    // otherwise grow forever, and a re-created event with the same id would inherit a stale one.
    for (const id of [...announced.keys()]) {
      if (!live.has(id)) {
        announced.delete(id);
        changed = true;
      }
    }

    if (changed && hydrated) persist();
  }

  const check = (): Promise<void> => {
    // Serialised rather than dropped: a slow filesystem must not skip a check, and two passes reading
    // the same file could each decide the same reminder is unannounced.
    running = (running ?? Promise.resolve()).then(runCheck).catch((error: unknown) => report(error));
    return running;
  };

  return {
    async hydrate() {
      if (hydrated) return;
      try {
        const blob = (await options.watermarks.read()) as Stored | null;
        const raw = blob?.announced;
        if (raw !== null && typeof raw === 'object' && raw !== undefined) {
          announced = new Map(
            Object.entries(raw).filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
          );
        }
      } catch (error) {
        // A missing file is the first run. An empty watermark map means every past reminder inside the
        // horizon is "unannounced" and fires late once — which is the honest reading of a fresh
        // profile that has just synced somebody's calendar in.
        report(error);
      }
      hydrated = true;
      await check();
    },

    start() {
      if (timer !== null) return;
      void check();
      timer = setTimer(() => void check(), CALENDAR_TICK_MS);
    },

    stop() {
      if (timer === null) return;
      clearTimer(timer);
      timer = null;
    },

    check,

    async upcoming() {
      const events = await options.readEvents().catch(() => [] as CalendarEvent[]);
      const at = now();
      return events
        .flatMap((event) => {
          const next = nextReminderAt(event, Math.max(at, announced.get(event.id) ?? 0), options.horizonDays);
          return next === null ? [] : [{ eventId: event.id, at: next }];
        })
        .sort((a, b) => a.at - b.at);
    },
  };
}
